import { Injectable, NgZone } from '@angular/core';
import { DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { QueryResult } from '@mode-switch/query';
import {
  BasicChannel,
  ColorTarget,
  EncChannel,
  EncField,
  EncFieldGroup,
  Encoding,
  MarkChannel,
  MarkType,
  VariableType,
} from '@mode-vegas/vegas';
import {
  EncodingHelpers,
  SwitchRenderModel,
  SwitchSchemaModel,
  TableEncodingHelpers,
  makeSummaryData,
} from '@mode/charts/util-vegas';
import { ChartDomain, ChartTypes, DataViewTypes, FlamingoTypes, ImmutableMap } from '@mode/shared/contract-common';
import { VizStorageService } from '@mode/shared/data-access-webapp';
import {
  generateFormatFromFlatTableFormat,
  getDefaultFieldFormatMapFromColumns,
  getDefaultGeneralFormat,
  getDefaultTableColumnSettingsFromColumns,
  isPresent,
} from '@mode/shared/util-js';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, first, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ChartFacade, DragFieldInfo } from '@mode/charts/contract';
import * as ChartSwitchActions from './chart/chart-switch.actions';
import * as ChartActions from './chart/chart.actions';
import * as ChartSelectors from './chart/chart.selectors';
import { DomainAppState } from './charts-domain.types';

@Injectable()
export class ChartService implements ChartFacade {
  constructor(
    private store: Store<DomainAppState>,
    actions$: Actions,
    private ngZone: NgZone,
    private dataViewFacade: DataViewFacade,
    private flamingoFacade: FlamingoFacade,
    private vizStorageService: VizStorageService,
    private toastService: ToastService
  ) {
    this.selectedToken$ = store.select(ChartSelectors.selectedToken).pipe(shareReplay(1));
    this.chartTokens$ = store.select(ChartSelectors.selectTokens).pipe(filter(isPresent));
    this.versions$ = store.select(ChartSelectors.selectVersions).pipe(filter(isPresent));
    this.histories$ = store.select(ChartSelectors.selectHistories);

    this.dragFieldNotifier = new BehaviorSubject({});
    this.dragField$ = this.dragFieldNotifier.asObservable();

    this.containerHasCharts$ = this.chartTokens$.pipe(map((tokens) => !tokens.isEmpty()));
    this.v2ChartTokens$ = combineLatest([this.chartTokens$, this.versions$]).pipe(
      map(([tokens, versions]) => tokens.filter((t) => versions.get(t) === 2)),
      shareReplay(1)
    );

    this.v1ChartTokens$ = combineLatest([this.chartTokens$, this.versions$]).pipe(
      map(([tokens, versions]) => tokens.filter((t) => versions.get(t) === 1)),
      shareReplay(1)
    );
    this.isDeletingChart$ = actions$.pipe(
      ofType(
        ChartSwitchActions.deleteChartFromBrowser,
        ChartSwitchActions.deleteChartFromBrowserFailure,
        ChartSwitchActions.deleteChartFromBrowserSuccess,
        ChartSwitchActions.deleteChartFromTabs,
        ChartSwitchActions.deleteChartFromTabsFailure,
        ChartSwitchActions.deleteChartFromTabsSuccess
      ),
      map((action) => {
        switch (action.type) {
          case ChartSwitchActions.deleteChartFromBrowser.type:
          case ChartSwitchActions.deleteChartFromTabs.type:
            return true;
          default:
            return false;
        }
      }),
      startWith(false),
      distinctUntilChanged(),
      shareReplay(1)
    );

    // different style from isDeletingChart$ for educational purposes
    this.isCreatingChart$ = merge(
      actions$.pipe(
        ofType(
          ChartSwitchActions.createDefaultChart,
          ChartSwitchActions.createVegasEncodingChart,
          ChartSwitchActions.createNewChart,
          ChartSwitchActions.createNewTable,
          ChartSwitchActions.cloneChartFromBrowser,
          ChartSwitchActions.cloneChartFromTab,
          ChartSwitchActions.cloneChartFromBuilder,
          ChartSwitchActions.cloneChartFromDesigner
        ),
        map(() => true)
      ),
      actions$.pipe(
        ofType(
          ChartSwitchActions.createDefaultChartFailure,
          ChartSwitchActions.createDefaultChartSuccess,
          ChartSwitchActions.createVegasEncodingChartSuccess,
          ChartSwitchActions.createVegasEncodingChartFailure,
          ChartSwitchActions.createNewChartFailure,
          ChartSwitchActions.createNewChartSuccess,
          ChartSwitchActions.createNewTableFailure,
          ChartSwitchActions.createNewTableSuccess,
          ChartSwitchActions.cloneChartFromBrowserSuccess,
          ChartSwitchActions.cloneChartFromBrowserFailure,
          ChartSwitchActions.cloneChartFromTabSuccess,
          ChartSwitchActions.cloneChartFromTabFailure,
          ChartSwitchActions.cloneChartFromBuilderSuccess,
          ChartSwitchActions.cloneChartFromBuilderFailure,
          ChartSwitchActions.cloneChartFromDesignerSuccess,
          ChartSwitchActions.cloneChartFromDesignerFailure
        ),
        map(() => false)
      )
    ).pipe(startWith(false), distinctUntilChanged(), shareReplay(1));

    /**
     * Emits a chart token whenever a new switch view based chart is successfully created.
     */
    this.newChartCreatedToken$ = actions$.pipe(
      ofType(
        ChartSwitchActions.createDefaultChartSuccess,
        ChartSwitchActions.createVegasEncodingChartSuccess,
        ChartSwitchActions.createNewChartSuccess,
        ChartSwitchActions.createNewTableSuccess,
        ChartSwitchActions.cloneChartFromBrowserSuccess,
        ChartSwitchActions.cloneChartFromTabSuccess,
        ChartSwitchActions.cloneChartFromBuilderSuccess,
        ChartSwitchActions.cloneChartFromDesignerSuccess
      ),
      map((action) => action.chart.token)
    );
    this.hasVizInStorage$ = this.vizStorageService.hasSavedViz();
    this.savedVizInStorage$ = this.vizStorageService.getSavedVizSnapshot();
  }

  private dragFieldNotifier: BehaviorSubject<DragFieldInfo | {}>;
  readonly dragField$: Observable<DragFieldInfo | {}>;

  readonly selectedToken$: Observable<ChartTypes.ChartFeatureState['selectedToken']>;
  readonly chartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  readonly versions$: Observable<ChartTypes.ChartFeatureState['versions']>;
  readonly histories$: Observable<ImmutableMap<ChartTypes.ChartHistory, string>>;

  readonly containerHasCharts$: Observable<boolean>;
  readonly v2ChartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  readonly v1ChartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  readonly isDeletingChart$: Observable<boolean>;
  readonly isCreatingChart$: Observable<boolean>;
  readonly newChartCreatedToken$: Observable<string>;
  readonly hasVizInStorage$: Observable<boolean>;
  readonly savedVizInStorage$: Observable<ChartTypes.ChartSnapshot>;

  /**
   * Observable creators
   */

  createSwitchViewTokenFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createSwitchViewTokenSelector(vizToken));
  }

  createQueryTokenFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createQueryTokenSelector(vizToken));
  }

  createChartSnapshot(vizToken: string) {
    return this.store.select(ChartSelectors.createChartSnapshotSelector(vizToken));
  }

  createChartVersionFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartVersionSelector(vizToken));
  }

  createIsValidFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createIsValidSelector(vizToken));
  }

  createIsCompleteFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createIsCompleteSelector(vizToken));
  }

  createChartTypeFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartTypeSelector(vizToken));
  }

  createEncodingFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createEncodingSelector(vizToken));
  }

  createChartFormatFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartFormatSelector(vizToken));
  }

  createPagingOffsetFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createPagingOffsetSelector(vizToken));
  }

  createChartFieldFormatMapFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartFieldFormatMapSelector(vizToken));
  }

  createChartFieldColorMapFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartFieldColorMapSelector(vizToken));
  }

  createPaletteFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createPaletteSelector(vizToken));
  }

  createChartFacetSizeMapFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartFacetSizeMapSelector(vizToken));
  }

  createChartTitleFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartTitleSelector(vizToken));
  }

  createChartDescriptionFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartDescriptionSelector(vizToken));
  }

  createChartTypeEncodingFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createChartTypeEncodingSelector(vizToken));
  }

  createIsAutoUpdateDisabledFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createIsAutoUpdateDisabledSelector(vizToken));
  }

  createManuallyApplyChangesFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createManuallyApplyChangesSelector(vizToken));
  }

  createUnappliedChangesFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createUnappliedChangesSelector(vizToken));
  }

  createEphemeralsFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createEphemeralsSelector(vizToken));
  }

  createCaptionFromVizToken(vizToken: string) {
    return this.store.select(ChartSelectors.createCaptionSelector(vizToken));
  }

  createChartsMetadataBySwitchViewToken(switchViewToken: string) {
    return this.store.select(ChartSelectors.createChartsMetadataBySwitchViewToken(switchViewToken));
  }

  createChartTokensBySwitchViewToken(switchViewToken: string) {
    return this.store.select(ChartSelectors.createChartTokensBySwitchViewToken(switchViewToken));
  }

  createIsVizFromImportedSwitchView(vizToken: string) {
    return this.createSwitchViewTokenFromVizToken(vizToken).pipe(
      filter(isPresent),
      switchMap((switchViewToken) =>
        this.dataViewFacade.createSwitchViewFeatureBySwitchViewToken(switchViewToken).pipe(
          filter(isPresent),
          map((switchViewFeature) => switchViewFeature.sourceType === DataViewTypes.SwitchViewSourceType.SwitchView)
        )
      )
    );
  }

  /**
   * Create the selector for initial flamingo plan effect state validated by
   * `isChartEncodingComplete`.
   */
  createCompleteFlamingoParams(vizToken: string) {
    return this.store.select(ChartSelectors.createFlamingoParamsSelector(vizToken)).pipe(
      filter(isPresent),
      filter(({ encoding, chartType }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType))
    );
  }

  /**
   * Create the selector for initial flamingo plan effect state validated by
   * `isChartEncodingComplete`.
   */
  createCompleteFlamingoParamsWithEmptyEncoding(vizToken: string) {
    return this.store.select(ChartSelectors.createFlamingoParamsSelector(vizToken)).pipe(filter(isPresent));
  }

  // Note - This currently uses the vizToken and gets the queryToken via this feature. This models
  // current behavior, though, we may want to be more explicit about a render model being generated
  // for a switchView and the selection for a viz is tied to the switchView, abstracting the queryToken
  // portion away. This will be relevant as we try to switch from queryToken to switchViewToken as name.
  createRenderDataModel(vizToken: string, planId: string) {
    return combineLatest([
      this.flamingoFacade.createPlanFromPlanId(planId),
      this.flamingoFacade.createResultFromPlanId(planId),
      this.dataViewFacade.model$,
      this.createQueryTokenFromVizToken(vizToken),
    ]).pipe(
      map(([plan, result, model, queryToken]) => {
        if (result != null && queryToken != null) {
          return SwitchRenderModel.make(model, queryToken, plan, result);
        }

        return null;
      })
    );
  }

  createManualSortDomainValues(refId: string, alias: string) {
    return this.flamingoFacade.createPlanIdFromRefId(refId).pipe(
      switchMap((planId) =>
        this.flamingoFacade.createReadyFromPlanId(planId).pipe(
          map((ready) => ({ planId, ready })),
          filter(({ ready }) => ready),
          switchMap(({ planId }) =>
            this.flamingoFacade.createDomainRecordsSelector(planId).pipe(
              map((domainHash) => {
                const domain = domainHash && domainHash[alias];
                if (domain === undefined) {
                  throw new Error(`Domain Hash returned undefined`);
                }
                if (domain?.type === Attr.Type.Discrete) {
                  return domain.values;
                } else {
                  throw new Error(`Expected discrete domain type but received ${domain?.type}`);
                }
              })
            )
          )
        )
      )
    );
  }

  // Note - there can only be 6 items in a combineLatest, so I bundled by grouping here. We could
  // build a more explicit abstraction around these sets of things, but not sure they're actually
  // reused anywhere at this point, so this seems simplest until this represents a reusable pattern.
  createFlamingoChartSummary(vizToken: string, planId: string) {
    return combineLatest([
      combineLatest([
        this.createChartTypeEncodingFromVizToken(vizToken),
        this.createChartFormatFromVizToken(vizToken),
        this.createQueryTokenFromVizToken(vizToken),
      ]),
      this.dataViewFacade.model$,
      combineLatest([
        this.flamingoFacade.createPlanFromPlanId(planId),
        this.flamingoFacade.createLoadingByPlanId(planId),
        this.flamingoFacade.createResultFromPlanId(planId),
      ]),
    ]).pipe(
      map(([[typeEncoding, format, queryToken], model, [plan, loading, result]]) => {
        if (typeEncoding != null && queryToken != null) {
          const { encoding, chartType } = typeEncoding;
          if (chartType !== 'table') {
            const isValid =
              EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
              EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
                0;

            if (isValid && !loading) {
              if (model != null && queryToken != null && plan != null && result != null && format != null) {
                const renderModel = SwitchRenderModel.make(model, queryToken, plan, result);
                if (renderModel != null) {
                  return makeSummaryData(renderModel, encoding);
                }
              }

              return null; // null means we expected data, but there was none, i.e. invalid chart or plan failed
            }
          }
        }

        return undefined;
      })
    );
  }

  /**
   * Action dispatchers
   */

  selectChart(token: string) {
    this.store.dispatch(ChartActions.selectChart({ token }));
  }

  loadChartsFromGfResources(queryToken: string, resources: ReadonlyArray<ChartTypes.ChartResource>) {
    this.store.dispatch(
      ChartActions.convertLoadedCharts({
        payload: resources,
        queryToken,
      })
    );
  }

  loadChartFromReportView(queryToken: string, resource: ReadonlyArray<ChartTypes.ChartResource>) {
    this.store.dispatch(
      ChartActions.loadReportViewChartSuccess({
        payload: resource,
        queryToken,
      })
    );
  }

  startFieldDrag(
    formula: CompiledFormula | null | undefined,
    source: EncChannel | 'field-list',
    fields?: string[],
    groupId?: string
  ) {
    this.ngZone.run(() => this.dragFieldNotifier.next({ formula, source, fields, groupId }));
  }

  endFieldDrag() {
    this.ngZone.run(() => this.dragFieldNotifier.next({}));
  }

  /**
   * For creating a default chart from the top tabs, optionally providing a chartType to initialize.
   */
  createNewChart(switchViewToken: string, queryToken: string, chartType: ChartTypes.ChartType = 'line'): void {
    this.store.dispatch(ChartSwitchActions.createNewChart({ switchViewToken, queryToken, chartType }));
  }

  /**
   * For creating a table chart from the top tabs.
   */
  createNewTable(switchViewToken: string, queryToken: string) {
    this.store.dispatch(ChartSwitchActions.createNewTable({ switchViewToken, queryToken }));
  }

  /**
   * For creating a default chart from the editor browser
   * @param switchViewToken
   */
  createDefaultChart(switchViewToken: string, queryToken: string) {
    this.store.dispatch(ChartSwitchActions.createDefaultChart({ switchViewToken, queryToken }));
  }

  createVegasEncodingChart(switchViewToken: string, queryToken: string) {
    this.store.dispatch(ChartSwitchActions.createVegasEncodingChart({ switchViewToken, queryToken }));
  }

  duplicateChartInBrowser(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.cloneChartFromBrowser({ token: chartToken }));
  }

  duplicateChartInTab(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.cloneChartFromTab({ token: chartToken }));
  }

  duplicateChartInDesigner(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.cloneChartFromDesigner({ token: chartToken }));
  }

  duplicateChartInBuilder(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.cloneChartFromBuilder({ token: chartToken }));
  }

  copyToVizStorage(chartToken: string) {
    this.createChartSnapshot(chartToken)
      .pipe(first())
      .subscribe((snapshot) => {
        try {
          this.vizStorageService.addViz(snapshot as ChartTypes.ChartSnapshot);
          this.toastService.success('Chart copied. Paste to an existing data source or chart in a Report.');
        } catch (err) {
          this.toastService.error('Failed to copy the chart.');
        }
      });
  }

  pasteFromVizStorage(switchViewToken: string, queryToken: string) {
    const snapshot = this.vizStorageService.getViz();
    if (snapshot) {
      this.store.dispatch(
        ChartSwitchActions.addChartFromVizStorage({
          switchViewToken,
          queryToken,
          snapshot,
        })
      );
    } else {
      this.toastService.error('Failed to paste the chart.');
    }
  }

  replaceFromVizStorage(token: string) {
    const snapshot = this.vizStorageService.getViz();
    if (snapshot) {
      this.store.dispatch(
        ChartSwitchActions.replaceFromVizStorage({
          token,
          snapshot,
        })
      );
      this.toastService.success('Chart pasted');
    } else {
      this.toastService.error('Failed to paste the chart.');
    }
  }

  updateChartType(chartToken: string, chartType: ChartTypes.ChartType) {
    this.store.dispatch(
      ChartSwitchActions.updateDesignerChartType({
        token: chartToken,
        chartType,
      })
    );
  }

  updateIsAutoUpdateDisabled(chartToken: string, disable: boolean) {
    this.store.dispatch(
      ChartSwitchActions.updateIsAutoUpdateDisabled({
        token: chartToken,
        isAutoUpdateDisabled: disable,
      })
    );
  }

  manuallyApplyChanges(chartToken: string) {
    this.store.dispatch(
      ChartSwitchActions.manuallyApplyChanges({
        token: chartToken,
      })
    );
  }

  updateCaption(chartToken: string, caption: ChartTypes.CaptionInfo) {
    this.store.dispatch(
      ChartSwitchActions.updateCaption({
        token: chartToken,
        caption: caption,
      })
    );
  }

  /**
   * For supporting editing the chart designer in the editor context
   * @param chartToken
   */

  updateTitleInDesigner(chartToken: string, title: string) {
    this.store.dispatch(ChartSwitchActions.updateDesignerChartTitle({ token: chartToken, title }));
  }

  updatePaletteInDesigner(chartToken: string, paletteToken: string) {
    this.store.dispatch(ChartSwitchActions.updateDesignerChartPalette({ token: chartToken, paletteToken }));
  }

  /**
   * @param chartToken
   */
  deleteChartInBrowser(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.deleteChartFromBrowser({ token: chartToken }));
  }

  /**
   * @param chartToken
   */
  deleteChartInTabs(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.deleteChartFromTabs({ token: chartToken }));
  }

  /**
   * @param chartToken
   */
  clearChart(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.clearChart({ token: chartToken }));
  }

  /**
   * @param chartToken
   */
  swapAxes(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.swapAxes({ token: chartToken }));
  }

  undo(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.createChartUndo({ token: chartToken }));
  }

  redo(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.createChartRedo({ token: chartToken }));
  }

  /**
   * @param chartToken
   * @param target
   */
  drillDown(chartToken: string, target: ChartTypes.MarkTarget) {
    this.store.dispatch(
      ChartSwitchActions.drillDown({
        token: chartToken,
        target,
      })
    );
  }

  /**
   * @param chartToken
   * @param forward
   */
  changeHierarchy(chartToken: string, forward: boolean) {
    this.store.dispatch(
      ChartSwitchActions.changeHierarchy({
        token: chartToken,
        forward,
      })
    );
  }

  /**
   * Performs a drill-down action on the target field, drilling
   * into the drill-to field.
   */
  dynamicDrillDown(
    chartToken: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ) {
    this.store.dispatch(
      ChartSwitchActions.dynamicDrillDown({
        token: chartToken,
        target,
        targetFieldId,
        drillToField,
        dateTruncUnit,
      })
    );
  }

  /**
   * @param chartToken
   * @param description
   */
  updateBigValueDescription(chartToken: string, description: string) {
    this.store.dispatch(
      ChartSwitchActions.updateBigValueDescription({
        token: chartToken,
        description,
      })
    );
  }

  /**
   * @param chartToken
   * @param label
   */
  updateBigValueTrendLabel(chartToken: string, label: string) {
    this.store.dispatch(
      ChartSwitchActions.updateBigValueTrendLabel({
        token: chartToken,
        label,
      })
    );
  }

  viewCalcFieldSetFieldToFormat(field: EncField | null) {
    this.store.dispatch(
      ChartSwitchActions.viewCalcFieldSetFieldToFormat({
        field,
      })
    );
  }

  viewCalcFieldAllSetFieldToFormat(field: EncField | null) {
    this.store.dispatch(
      ChartSwitchActions.viewCalcFieldAllSetFieldToFormat({
        field,
      })
    );
  }

  vizSettingsComponentRemoveSelectedFieldToFormat() {
    this.store.dispatch(ChartSwitchActions.vizSettingsComponentRemoveSelectedFieldToFormat());
  }

  sharedAxisEncodingSetGroupToFormat(group: EncFieldGroup) {
    this.store.dispatch(
      ChartSwitchActions.sharedAxisEncodingSetGroupToFormat({
        group,
      })
    );
  }

  /**
   * @param chartToken
   * @param enc
   * @param field
   * @param channel
   * @param leftMark
   * @param rightMark
   */
  addFieldAndMakeDual(
    chartToken: string,
    enc: Encoding,
    field: CompiledFormula,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.addFieldAndMakeDual(enc, field, channel, leftMark, rightMark).encoding;
    this.store.dispatch(
      ChartSwitchActions.updateDesignerChartEncoding({
        token: chartToken,
        encoding: newEncoding,
      })
    );
  }

  /**
   * @param chartToken
   * @param enc
   * @param field
   * @param channel
   * @param leftMark
   * @param rightMark
   */
  moveFieldAndMakeDual(
    chartToken: string,
    enc: Encoding,
    field: CompiledField,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.moveFieldAndMakeDual(enc, field, channel, leftMark, rightMark).encoding;
    this.store.dispatch(
      ChartSwitchActions.updateDesignerChartEncoding({
        token: chartToken,
        encoding: newEncoding,
      })
    );
  }

  clearFilters(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.clearFilters({ token: chartToken }));
  }

  clearSorts(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.clearSorts({ token: chartToken }));
  }

  clearFormatting(chartToken: string) {
    this.store.dispatch(ChartSwitchActions.clearFormatting({ token: chartToken }));
  }

  quickSort(chartToken: string, descending: boolean) {
    this.store.dispatch(ChartSwitchActions.quickSort({ token: chartToken, descending }));
  }

  removeSort(chartToken: string, formula: string) {
    this.store.dispatch(ChartSwitchActions.removeSort({ token: chartToken, formula }));
  }

  updateChartFormat(chartToken: string, format: ChartTypes.ChartFormat) {
    this.store.dispatch(ChartSwitchActions.updateFormat({ token: chartToken, format }));
  }

  updateTextFormat(
    chartToken: string,
    formula: string,
    pane: ChartTypes.FieldPaneTypes,
    format: ChartTypes.TextFormat
  ) {
    this.store.dispatch(ChartSwitchActions.updateFieldFormat({ token: chartToken, formula, pane, format }));
  }

  updateTheme(chartToken: string, theme: string, colorPaletteToken = '', palette: string[] = [], showAlert = false) {
    this.store.dispatch(
      ChartSwitchActions.updateDesignerColors({
        token: chartToken,
        colorPaletteToken,
        palette,
        showAlert,
      })
    );
  }

  updateBaseFormatDisplayName(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.BaseFieldFormat
  ) {
    this.store.dispatch(
      ChartSwitchActions.updateBaseFormatDisplayName({
        token: chartToken,
        format,
        formula,
        varType,
      })
    );
  }

  updateAxisFormat(chartToken: string, formula: string, varType: VariableType, format: ChartTypes.AxisFieldFormat) {
    this.store.dispatch(
      ChartSwitchActions.updateAxisFieldFormat({
        token: chartToken,
        format,
        formula,
        varType,
      })
    );
  }

  updatePaneFormat(chartToken: string, formula: string, varType: VariableType, format: ChartTypes.PaneFieldFormat) {
    this.store.dispatch(
      ChartSwitchActions.updatePaneFieldFormat({
        format,
        formula,
        varType,
        token: chartToken,
      })
    );
  }

  updateHeaderFormat(chartToken: string, formula: string, varType: VariableType, format: ChartTypes.HeaderFieldFormat) {
    this.store.dispatch(
      ChartSwitchActions.updateHeaderFieldFormat({
        format,
        formula,
        varType,
        token: chartToken,
      })
    );
  }

  updateLegendFormat(chartToken: string, formula: string, varType: VariableType, format: ChartTypes.LegendFieldFormat) {
    this.store.dispatch(
      ChartSwitchActions.updateLegendFieldFormat({
        format,
        varType,
        formula,
        token: chartToken,
      })
    );
  }

  updateColorField(
    chartToken: string,
    formula: string,
    varType: VariableType,
    colorPaletteToken = '',
    palette: string[] = [],
    series?: string,
    colorMap: ChartTypes.ColorEntries = {},
    gradient?: ChartTypes.ContinuousGradient,
    unassignedColor?: string,
    colorTarget?: ColorTarget
  ) {
    this.store.dispatch(
      ChartSwitchActions.updateDesignerFieldColor({
        token: chartToken,
        formula,
        varType,
        colorPaletteToken,
        palette,
        series,
        colorMap,
        gradient,
        unassignedColor,
        colorTarget,
      })
    );
  }

  updateFacetSizes(chartToken: string, facetSizes: ChartTypes.FacetSizeMap) {
    this.store.dispatch(
      ChartSwitchActions.updateFacetSizes({
        facetSizes,
        token: chartToken,
      })
    );
  }

  requestByManualSortDomain(
    token: string,
    refId: string,
    field: EncField,
    encoding: Encoding,
    items: unknown[] | undefined
  ) {
    this.store.dispatch(
      ChartSwitchActions.viewCalcModalManualSortDomainRequest({ token, encoding, refId, field, items })
    );
  }

  updateChartFromSnapshot(token: string, model?: FlamingoTypes.ModeModel) {
    this.store.dispatch(
      ChartSwitchActions.updateChartFromSnapshot({
        model,
        token,
      })
    );
  }

  setEphemeral(token: string) {
    this.store.dispatch(
      ChartSwitchActions.setEphemeral({
        token,
      })
    );
  }

  fetchFilterDomainValues(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    field: CompiledField,
    refId: string,
    domainAlias: string,
    includeRelevantValuesOnly: boolean
  ) {
    this.store.dispatch(
      ChartSwitchActions.filterGetDomainValuesRequest({
        queryRunToken,
        view,
        encoding,
        field,
        refId,
        domainAlias,
        withFilters: includeRelevantValuesOnly ? 'otherFields' : 'none',
      })
    );
  }

  fetchColorDomainValues(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    field: CompiledField,
    refId: string,
    domainAlias: string,
    series?: string
  ) {
    this.store.dispatch(
      ChartSwitchActions.colorsGetDomainValuesRequest({
        queryRunToken,
        view,
        encoding,
        field,
        refId,
        domainAlias,
        withFilters: 'all',
        series,
      })
    );
  }

  createSelectDomainValues(refId: string, domainAlias: string) {
    return this.flamingoFacade.createPlanIdFromRefId(refId).pipe(
      switchMap((planId) =>
        this.flamingoFacade.createReadyFromPlanId(planId).pipe(
          map((ready) => ({ planId, ready })),
          filter(({ ready }) => ready),
          switchMap(({ planId }) =>
            this.flamingoFacade.createDomainRecordsSelector(planId).pipe(
              map((result) => {
                return result?.[domainAlias];
              })
            )
          )
        )
      )
    );
  }

  createErrorFromDomainQuery(refId: string) {
    return this.flamingoFacade
      .createPlanIdFromRefId(refId)
      .pipe(switchMap((planId) => this.flamingoFacade.createErrorFromPlanId(planId)));
  }

  /**
   * Fetches the domain range for a continuous field in a Mark channel. The range for
   * a continuous field can vary across series due to different levels of detail in
   * the other Mark channels.
   *
   * Example:
   *
   * Series 1
   *  Color:  SUM(price)
   *  Detail: shipping_mode
   *
   * Range for SUM(price): 4147.71 - 178449.01
   *
   * Series 2
   *  Color: SUM(price)
   *  Text:  payment_state
   *  Detail: shipping_state
   *
   * Range for SUM(price): 5.99 - 48439.93
   *
   * Total Range for SUM(price): 5.99 - 178449.01
   *
   * This function is specifically for calculating the domain for continuous field
   * in a mark channel since that use-case poses some very specific challenges. All
   * other domain requests should use 'fetchColorDomainValues' or 'fetchFilterDomainValues'.
   */
  fetchContinuousMarkDomainRange(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    refId: string,
    field: CompiledField,
    channel: MarkChannel
  ) {
    if (field.isDiscrete) {
      throw Error(
        'fetchContinuousMarkDomainRange is for continuous fields only. Use fetchColorDomainValues or fetchFilterDomainValues instead.'
      );
    }
    if (field.alias === undefined) {
      throw Error('field alias is required');
    }

    this.store.dispatch(
      ChartSwitchActions.continuousMarkDomainRangeRequest({
        queryRunToken,
        view,
        encoding,
        refId,
        alias: field.alias,
        channel,
      })
    );
  }

  /**
   * Returns an Observable of the min/max calculated from 'fetchContinuousMarkDomainRange'.
   * This requires the ids of the fields for which the unioned, continuous domain is calculated.
   * These ids are for the fields on the common mark channel across series.
   */
  createSelectContinuousMarkDomainRange(vizToken: string, refId: string, fieldIds: string[]) {
    return this.flamingoFacade.createPlanIdFromRefId(refId).pipe(
      switchMap((planId) =>
        combineLatest([this.createEncodingFromVizToken(vizToken), this.createRenderDataModel(vizToken, planId)]).pipe(
          filter(([encoding, model]) => isPresent(encoding) && isPresent(model)),
          map(([encoding, model]) => {
            if (encoding == null || model == null) {
              return { min: Number.NaN, max: Number.NaN };
            }

            const seriesFields = encoding.marks.channel !== '*' ? encoding.marks.series.map((s) => s.field) : ['*'];
            const allMinMax = seriesFields.reduce<number[]>((minMax, seriesField) => {
              const seriesData = model.getSeriesData(seriesField);
              if (seriesData) {
                const seriesSchema = model.getSeriesSchema(seriesField);
                const relevantFields = fieldIds.filter((fieldId) =>
                  seriesSchema.some((schemaField) => schemaField.name === fieldId)
                );
                minMax = minMax.concat(
                  this.calculateSeriesMinMaxAcrossFields(seriesData, relevantFields)
                    .filter((value) => !Number.isNaN(value))
                    .flat()
                );
              }
              return minMax;
            }, []);

            return allMinMax.length > 0
              ? { min: Math.min(...allMinMax), max: Math.max(...allMinMax) }
              : { min: Number.NaN, max: Number.NaN };
          })
        )
      )
    );
  }

  private calculateSeriesMinMaxAcrossFields(seriesData: readonly Readonly<QueryResult.Item>[], fieldIds: string[]) {
    return seriesData.reduce<number[]>(
      (seriesMinMax, data) => {
        fieldIds.forEach((fieldId) => {
          const value = data[fieldId];
          if (value == null) {
            return seriesMinMax;
          }
          if (Number.isNaN(seriesMinMax[0]) || value < seriesMinMax[0]) {
            seriesMinMax[0] = value;
          }
          if (Number.isNaN(seriesMinMax[1]) || value > seriesMinMax[1]) {
            seriesMinMax[1] = value;
          }
        });
        return seriesMinMax;
      },
      [Number.NaN, Number.NaN]
    );
  }

  createTableChartEncoding() {
    return EncodingHelpers.initEncoding('table').encoding;
  }

  createTableColumns(model: FlamingoTypes.ModeModel) {
    const attrs = model.views[0].attrs;
    return attrs.map((attr) => ({
      alias: attr.name,
      name: attr.name,
      source: `[${attr.name}]`,
      type: attr.formula.type,
    }));
  }

  createTableChartFormats(columns: FlamingoTypes.ResultColumns) {
    const format = generateFormatFromFlatTableFormat({
      general: {
        ...getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.FlatTableViz),
      },
      columns: getDefaultTableColumnSettingsFromColumns(columns),
    });

    const fieldsFormat = getDefaultFieldFormatMapFromColumns(columns);

    return {
      format,
      fieldsFormat,
    };
  }

  addRemainingColumnsToTable(enc: Encoding, model: FlamingoTypes.ModeModel, queryToken: string) {
    return TableEncodingHelpers.addRemainingColumns(enc, model, queryToken);
  }

  viewUnaggregatedChartData(token: string) {
    return this.store.dispatch(ChartActions.viewUnaggregatedChartData({ chartToken: token }));
  }

  importDatasetWithChartSuccess(
    accountId: string,
    reportId: string,
    switchViewToken: string,
    vizToken: string,
    queryToken: string,
    chart: ChartDomain.Chart
  ) {
    return this.store.dispatch(
      ChartSwitchActions.importDatasetWithChartSuccess({
        accountId,
        reportId,
        switchViewToken,
        vizToken,
        queryToken,
        chart,
      })
    );
  }

  getBinSize(token: string, columnName: string, refId: string) {
    this.store.dispatch(
      ChartSwitchActions.getBinSize({
        token,
        columnName,
        isDesignerView: false,
        refId,
      })
    );
  }

  createBinSizeToFieldMapFromVizToken(token: string) {
    return this.flamingoFacade.createBinSizeToFieldMapFromVizToken(token).pipe(filter(isPresent));
  }

  createErrorFromBinSizeQuery(refId: string) {
    return this.flamingoFacade
      .createPlanIdFromRefId(refId)
      .pipe(switchMap((planId) => this.flamingoFacade.createErrorFromPlanId(planId)));
  }
}
